import { CheckCircle2, Clock, Copy, FilePlus2, GitCommitHorizontal, Rocket, Trash2 } from "lucide-react";
import { Card, CardHeader } from "@/ui";
import { useAuditLog } from "@/data/hooks";
import type { AuditEntry } from "@/data/services";
import { SkeletonLine } from "./parts";

/** Friendly label + icon per audit action. Falls back to the raw action. */
const ACTION_META: Record<string, { label: string; Icon: typeof Rocket }> = {
  "page.publish": { label: "Published", Icon: Rocket },
  "page.version.create": { label: "Saved a version", Icon: GitCommitHorizontal },
  "page.create": { label: "Created page", Icon: FilePlus2 },
  "page.duplicate": { label: "Duplicated page", Icon: Copy },
  "page.delete": { label: "Deleted page", Icon: Trash2 },
};

/** "page:page_homepage" → "page_homepage" (drop the kind prefix for display). */
function entityLabel(entity: string): string {
  const i = entity.indexOf(":");
  return i >= 0 ? entity.slice(i + 1) : entity;
}

function ago(iso: string): string {
  const t = new Date(iso).getTime();
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function Row({ e }: { e: AuditEntry }) {
  const meta = ACTION_META[e.action] ?? { label: e.action, Icon: CheckCircle2 };
  const Icon = meta.Icon;
  const env = typeof e.metadata?.environment === "string" ? (e.metadata.environment as string) : null;
  return (
    <div className="flex items-start gap-3 px-5 py-3.5">
      <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-paper-2 text-muted dark:bg-white/5">
        <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[14px] leading-snug text-ink-strong">
          {meta.label}
          {env ? <span className="text-muted"> to {env}</span> : null}{" "}
          <span className="font-mono text-[12px] text-muted">{entityLabel(e.entity)}</span>
        </p>
        <p className="text-[12px] text-subtle">{e.actor}</p>
      </div>
      <span className="mt-0.5 shrink-0 font-mono text-[11px] tabular-nums text-subtle">{ago(e.created_at)}</span>
    </div>
  );
}

/** Recent activity (audit log) for the workspace — who published/changed what. */
export function ActivitySection() {
  const q = useAuditLog({ limit: 50 });
  const items = q.data?.items ?? [];

  return (
    <Card className="overflow-hidden">
      <CardHeader title="Recent activity" eyebrow="Audit log" className="px-5 pt-4" />
      {q.isLoading ? (
        <div className="space-y-3 px-5 py-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="h-7 w-7 shrink-0 animate-pulse rounded-full bg-paper-2 dark:bg-white/5" />
              <div className="flex-1 space-y-1.5">
                <SkeletonLine className="h-3.5 w-52" />
                <SkeletonLine className="h-3 w-28" />
              </div>
            </div>
          ))}
        </div>
      ) : q.isError ? (
        <div className="px-5 py-10 text-center">
          <Clock className="mx-auto mb-2 h-6 w-6 text-subtle" strokeWidth={1.5} />
          <p className="text-[14px] text-muted">Couldn&rsquo;t load activity</p>
          <p className="mt-0.5 text-[12px] text-subtle">Sign in, or check your connection.</p>
        </div>
      ) : items.length === 0 ? (
        <div className="px-5 py-10 text-center">
          <Clock className="mx-auto mb-2 h-6 w-6 text-subtle" strokeWidth={1.5} />
          <p className="text-[14px] text-muted">No activity yet</p>
          <p className="mt-0.5 text-[12px] text-subtle">Publishes and changes show up here.</p>
        </div>
      ) : (
        <div className="divide-y divide-hairline/60 border-t border-hairline">
          {items.map((e) => (
            <Row key={e.id} e={e} />
          ))}
        </div>
      )}
    </Card>
  );
}
